import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Compass, UtensilsCrossed, Newspaper, CalendarCheck, MailQuestion,
  Plus, Pencil, Trash2, Home, Star, Check, X, Sparkles, ShieldCheck,
} from "lucide-react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import StatusBadge from "@/components/StatusBadge";
import RefundControl from "@/components/RefundControl";
import VillaAdminPanel from "@/components/VillaAdminPanel";
import SecurityPanel from "@/components/SecurityPanel";
import FoodAdminPanel from "@/components/FoodAdminPanel";
import { Label, Input, Textarea, Select, PrimaryButton, GhostButton } from "@/components/fields";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "enquiries", label: "Enquiries", icon: MailQuestion },
  { id: "services", label: "Services", icon: Sparkles },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "villa", label: "Villa", icon: Home },
  { id: "excursions", label: "Excursions", icon: Compass },
  { id: "restaurants", label: "Restaurants", icon: UtensilsCrossed },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "food", label: "Food", icon: UtensilsCrossed },
  { id: "security", label: "Security", icon: ShieldCheck },
];

const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const ENQUIRY_STATUSES = ["new", "contacted", "confirmed", "declined"];

const SERVICE_PRICE_FIELDS = [
  { key: "chef_two", label: "Chef — Table for Two (2 guests)" },
  { key: "chef_together", label: "Chef — Taste & Togetherness (4 guests)" },
  { key: "chef_gathering", label: "Chef — Island Gathering (6 guests)" },
  { key: "massage_pause", label: "Massage — Island Pause (30 min)" },
  { key: "massage_escape", label: "Massage — Paradise Escape (1 hour)" },
];

const th = "text-left text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 pb-3 pr-4";
const td = "py-3.5 pr-4 text-sm align-top";

const EMPTY_EXCURSION = { title: "", title_es: "", category: "Water Sports", price: 0, duration: "", difficulty: "Easy", image: "", includes: "", includes_es: "", description: "", description_es: "", active: true, seasonal_uplifts: [] };
const EMPTY_RESTAURANT = { name: "", cuisine: "Caribbean", price_level: "$$", area: "Cabarete", description: "", description_es: "", recommended_dish: "", recommended_dish_es: "", image: "", maps_url: "" };
const EMPTY_POST = { title: "", title_es: "", category: "What's Hot", excerpt: "", excerpt_es: "", content: "", content_es: "", image: "", tags: "" };

function StatusSelect({ value, options, onChange, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold capitalize outline-none focus:ring-2 focus:ring-terracotta/40"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function Stars({ n }) {
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={13} fill={i < n ? "#E8A838" : "none"} strokeWidth={i < n ? 0 : 1.5} className={i < n ? "text-gold" : "text-ink/20"} />
      ))}
    </span>
  );
}

function ReviewReplyEditor({ review, onDone }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState(review.owner_reply || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/reviews/${review.id}/reply`, { reply });
      toast.success(reply.trim() ? "Reply published" : "Reply removed");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      {review.owner_reply && !open && (
        <div className="rounded-xl bg-teal/5 border-l-2 border-teal px-4 py-2.5 mb-2" data-testid={`review-reply-view-${review.id}`}>
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-teal mb-1">Your reply (public)</p>
          <p className="text-xs text-ink/60 italic">{review.owner_reply}</p>
        </div>
      )}
      {open ? (
        <div className="space-y-2">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            data-testid={`review-reply-input-${review.id}`}
            placeholder="Gracias por quedarte con nosotros…"
            className="min-h-[70px] text-sm"
          />
          <div className="flex gap-2">
            <PrimaryButton data-testid={`review-reply-save-${review.id}`} onClick={save} disabled={busy} className="!px-4 !py-2 text-xs">
              {busy ? "Saving…" : "Save reply"}
            </PrimaryButton>
            <GhostButton data-testid={`review-reply-cancel-${review.id}`} onClick={() => { setOpen(false); setReply(review.owner_reply || ""); }} className="!px-4 !py-2 text-xs">
              Cancel
            </GhostButton>
          </div>
        </div>
      ) : (
        <button data-testid={`review-reply-open-${review.id}`} onClick={() => setOpen(true)} className="text-xs font-semibold text-teal hover:underline">
          {review.owner_reply ? "Edit reply" : "Reply publicly"}
        </button>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user, loading, isAdmin } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [excursions, setExcursions] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [servicePrices, setServicePrices] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState("");

  const loadAll = async () => {
    const [s, ex, re, po, bo, en, rv, pr, sr, pr2] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/excursions"),
      api.get("/admin/restaurants"),
      api.get("/admin/posts"),
      api.get("/admin/bookings"),
      api.get("/admin/enquiries"),
      api.get("/admin/reviews"),
      api.get("/admin/villa/periods"),
      api.get("/admin/service-requests"),
      api.get("/services/pricing"),
    ]);
    setStats(s.data);
    setExcursions(ex.data);
    setRestaurants(re.data);
    setPosts(po.data);
    setBookings(bo.data);
    setEnquiries(en.data);
    setReviews(rv.data);
    setPeriods(pr.data);
    setServiceRequests(sr.data);
    setServicePrices(pr2.data.prices || {});
  };

  useEffect(() => {
    if (isAdmin) loadAll().catch((err) => toast.error(formatApiError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-4 py-32 text-ink/40 text-sm" data-testid="admin-loading">Opening the owner panel…</div>;
  }
  if (!user) return <Navigate to="/auth" state={{ from: "/admin" }} replace />;
  if (!isAdmin) return <Navigate to="/account" replace />;

  const openEditor = (type, item) => {
    const empty = type === "excursions" ? EMPTY_EXCURSION : type === "restaurants" ? EMPTY_RESTAURANT : EMPTY_POST;
    const base = item ? { ...item } : { ...empty };
    if (type === "excursions") {
      base.includes = (base.includes || []).join(", ");
      base.includes_es = (base.includes_es || []).join(", ");
      base.title_es = base.title_es || "";
      base.description_es = base.description_es || "";
      base.seasonal_uplifts = base.seasonal_uplifts || [];
    }
    if (type === "restaurants") {
      base.description_es = base.description_es || "";
      base.recommended_dish_es = base.recommended_dish_es || "";
    }
    if (type === "posts") {
      base.tags = (base.tags || []).join(", ");
      base.title_es = base.title_es || "";
      base.excerpt_es = base.excerpt_es || "";
      base.content_es = base.content_es || "";
    }
    setForm(base);
    setEditing({ type, item });
  };

  const saveEditor = async () => {
    setSaving(true);
    const { type, item } = editing;
    const payload = { ...form };
    if (type === "excursions") {
      payload.price = Number(payload.price) || 0;
      payload.includes = payload.includes.split(",").map((s) => s.trim()).filter(Boolean);
      payload.includes_es = payload.includes_es.split(",").map((s) => s.trim()).filter(Boolean);
      payload.seasonal_uplifts = (payload.seasonal_uplifts || [])
        .filter((u) => u.period_id && Number(u.percent) > 0)
        .map((u) => ({ period_id: u.period_id, percent: Number(u.percent) }));
    }
    if (type === "posts") payload.tags = payload.tags.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (item) await api.put(`/admin/${type}/${item.id}`, payload);
      else await api.post(`/admin/${type}`, payload);
      toast.success(item ? "Updated" : "Created");
      setEditing(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${type}/${id}`);
      toast.success("Deleted");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (type, id, status) => {
    try {
      await api.patch(`/admin/${type}/${id}`, { status });
      toast.success(`Marked as ${status}`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setReviewApproval = async (id, approved) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { approved });
      toast.success(approved ? "Review published" : "Review unpublished");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statCards = stats
    ? [
        { label: "Excursion revenue", value: `$${stats.revenue.toLocaleString()}`, testid: "stat-revenue" },
        { label: "Deposits collected", value: `$${stats.deposits_collected?.toLocaleString() ?? 0}`, testid: "stat-deposits" },
        { label: "Total bookings", value: stats.total_bookings, testid: "stat-bookings" },
        { label: "Pending bookings", value: stats.pending_bookings, testid: "stat-pending" },
        { label: "Villa enquiries", value: stats.total_enquiries, testid: "stat-enquiries" },
        { label: "Excursions live", value: stats.excursions, testid: "stat-excursions" },
        { label: "Journal posts", value: stats.posts, testid: "stat-posts" },
        { label: "Registered guests", value: stats.guests, testid: "stat-guests" },
      ]
    : [];

  const field = (label, key, props = {}) => (
    <div key={key}>
      <Label>{label}</Label>
      <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`form-${key}-input`} {...props} />
    </div>
  );

  const aiDraft = async (key, kind) => {
    const topic = (form.title || form.name || "").trim();
    if (!topic) return toast.error("Add a title/name first so the AI knows what to write about");
    setAiBusy(key);
    try {
      const { data } = await api.post("/admin/ai/draft", { kind, topic, lang: key.endsWith("_es") ? "es" : "en" });
      setForm((f) => ({ ...f, [key]: data.text }));
      toast.success("AI draft ready — tweak it as you like");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy("");
    }
  };

  const area = (label, key, tall = false, aiKind = null) => (
    <div key={key}>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        {aiKind && (
          <button
            type="button"
            data-testid={`ai-draft-${key}`}
            disabled={aiBusy === key}
            onClick={() => aiDraft(key, aiKind)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-terracotta hover:text-gold transition-colors disabled:opacity-40 mb-1.5"
          >
            <Sparkles size={11} /> {aiBusy === key ? "Drafting…" : "AI draft"}
          </button>
        )}
      </div>
      <Textarea value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`form-${key}-input`} className={tall ? "min-h-[180px]" : ""} />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-16" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-terracotta">Owner panel</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-2">Control center</h1>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-8" data-testid="admin-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === id ? "bg-night text-sand" : "bg-white border border-ink/10 text-ink/70 hover:border-terracotta"
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
          {statCards.map((c) => (
            <div key={c.label} data-testid={c.testid} className="bg-white rounded-3xl border border-terracotta/10 p-6">
              <p className="font-serif text-3xl text-ink">{c.value}</p>
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 mt-2">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 overflow-x-auto" data-testid="admin-bookings">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr className="border-b border-ink/10">
                <th className={th}>Guest</th><th className={th}>Excursion</th><th className={th}>Date & time</th>
                <th className={th}>Pick-up</th><th className={th}>Total</th><th className={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-ink/5" data-testid={`admin-booking-row-${b.id}`}>
                  <td className={td}><p className="font-medium">{b.user_name}</p><p className="text-xs text-ink/45">{b.contact_phone}</p></td>
                  <td className={td}>{b.excursion_title}<p className="text-xs text-ink/45">{b.guests} guests</p></td>
                  <td className={td}>{fmtDate(b.date)}<p className="text-xs text-ink/45">{b.time_slot}</p></td>
                  <td className={td}><p className="text-xs">{b.pickup_location}</p></td>
                  <td className={td}>
                    <p className="font-semibold text-terracotta">${b.total_price}</p>
                    {b.deposit_status && b.deposit_status !== "unpaid" && (
                      <p className="text-xs text-teal capitalize mt-1">Deposit: {b.deposit_status.replace("_", " ")}</p>
                    )}
                  </td>
                  <td className={td}>
                    <div className="flex flex-col items-start gap-2">
                      <StatusSelect testid={`booking-status-${b.id}`} value={b.status} options={BOOKING_STATUSES} onChange={(s) => setStatus("bookings", b.id, s)} />
                      <RefundControl kind="booking" item={b} onDone={loadAll} />
                    </div>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-sm text-ink/40">No bookings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "enquiries" && (
        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 overflow-x-auto" data-testid="admin-enquiries">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-ink/10">
                <th className={th}>Guest</th><th className={th}>Dates</th><th className={th}>Guests</th>
                <th className={th}>Quote</th><th className={th}>Message</th><th className={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((q) => (
                <tr key={q.id} className="border-b border-ink/5" data-testid={`admin-enquiry-row-${q.id}`}>
                  <td className={td}><p className="font-medium">{q.name}</p><p className="text-xs text-ink/45">{q.email}</p></td>
                  <td className={td}>{fmtDate(q.check_in)} → {fmtDate(q.check_out)}</td>
                  <td className={td}>{q.guests}</td>
                  <td className={td}>
                    {q.quoted_total ? <p className="font-semibold text-terracotta">${q.quoted_total.toLocaleString()}</p> : "—"}
                    {q.deposit_status && q.deposit_status !== "unpaid" && (
                      <p className="text-xs text-teal capitalize mt-1">Deposit: {q.deposit_status.replace("_", " ")}</p>
                    )}
                  </td>
                  <td className={td}><p className="text-xs text-ink/55 max-w-[200px]">{q.message || "—"}</p></td>
                  <td className={td}>
                    <div className="flex flex-col items-start gap-2">
                      <StatusSelect testid={`enquiry-status-${q.id}`} value={q.status} options={ENQUIRY_STATUSES} onChange={(s) => setStatus("enquiries", q.id, s)} />
                      <RefundControl kind="enquiry" item={q} onDone={loadAll} />
                    </div>
                  </td>
                </tr>
              ))}
              {enquiries.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-sm text-ink/40">No enquiries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "services" && (
        <div className="space-y-6" data-testid="admin-services-tab">
          <div className="bg-white rounded-3xl border border-terracotta/10 p-6 sm:p-8" data-testid="service-pricing-card">
            <h3 className="font-serif text-2xl text-ink mb-2">Service pricing</h3>
            <p className="text-xs text-ink/45 leading-relaxed mb-5">
              Set a price (USD) per option. Priced options let guests pay online right after requesting — deposit or full amount. Leave blank to keep it "price upon request".
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SERVICE_PRICE_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input
                    type="number" min="0" data-testid={`price-input-${f.key}`}
                    value={servicePrices[f.key] ?? ""}
                    onChange={(e) => setServicePrices({ ...servicePrices, [f.key]: e.target.value })}
                    placeholder="Price upon request"
                  />
                </div>
              ))}
            </div>
            <PrimaryButton
              data-testid="service-pricing-save-btn" className="mt-5" disabled={savingPrices}
              onClick={async () => {
                setSavingPrices(true);
                try {
                  const payload = {};
                  for (const f of SERVICE_PRICE_FIELDS) payload[f.key] = Number(servicePrices[f.key]) || 0;
                  const { data } = await api.put("/admin/services/pricing", { prices: payload });
                  setServicePrices(data.prices);
                  toast.success("Service prices saved");
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setSavingPrices(false);
                }
              }}
            >
              {savingPrices ? "Saving…" : "Save prices"}
            </PrimaryButton>
          </div>
          <div className="bg-white rounded-3xl border border-terracotta/10 p-6 overflow-x-auto" data-testid="admin-services">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-ink/10">
                <th className={th}>Guest</th><th className={th}>Service</th><th className={th}>Date</th>
                <th className={th}>Guests</th><th className={th}>Notes</th><th className={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {serviceRequests.map((r) => (
                <tr key={r.id} className="border-b border-ink/5" data-testid={`admin-service-row-${r.id}`}>
                  <td className={td}>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs text-ink/45">{r.email}</p>
                    {r.phone && <p className="text-xs text-ink/45">{r.phone}</p>}
                  </td>
                  <td className={td}>
                    <p className="font-medium">{r.service_name}</p>
                    <p className="text-xs text-ink/45">{r.option}{r.experience ? ` · ${r.experience}` : ""}</p>
                    {r.total_price ? (
                      <p className="text-xs mt-1">
                        <span className="font-semibold text-terracotta">${r.total_price}</span>
                        <span className={`ml-1.5 ${r.deposit_status === "paid" ? "text-teal" : "text-ink/40"}`}>
                          {r.deposit_status === "paid" ? (r.paid_in_full ? "Paid in full" : "Deposit paid") : "Unpaid"}
                        </span>
                      </p>
                    ) : (
                      <p className="text-[11px] text-gold mt-1 font-mono uppercase tracking-wider">Price upon request</p>
                    )}
                  </td>
                  <td className={td}>{fmtDate(r.date)}{r.time ? ` · ${r.time}` : ""}</td>
                  <td className={td}>{r.guests}</td>
                  <td className={td}><p className="text-xs text-ink/55 max-w-[200px]">{r.notes || "—"}</p></td>
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <StatusSelect testid={`service-status-${r.id}`} value={r.status} options={ENQUIRY_STATUSES} onChange={(s) => setStatus("service-requests", r.id, s)} />
                      <button data-testid={`delete-service-${r.id}`} onClick={() => remove("service-requests", r.id)} className="p-1.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Delete request">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {serviceRequests.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-sm text-ink/40">No service requests yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "reviews" && (
        <div className="space-y-4" data-testid="admin-reviews">
          {reviews.length === 0 && (
            <div className="bg-white rounded-3xl border border-terracotta/10 p-10 text-center text-sm text-ink/40">
              No reviews yet. Guests with a booking or stay can leave one from "My trips".
            </div>
          )}
          {reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-row-${r.id}`} className="bg-white rounded-3xl border border-terracotta/10 p-6 flex flex-wrap items-center gap-4">
              <div className="grow min-w-[220px]">
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-sm">{r.name}</p>
                  <Stars n={r.rating} />
                  <StatusBadge status={r.approved ? "confirmed" : "pending"} />
                </div>
                <p className="text-sm text-ink/60 mt-2 leading-relaxed max-w-2xl">“{r.text}”</p>
                <p className="text-xs text-ink/40 mt-1.5">{fmtDate(r.created_at)}</p>
                <ReviewReplyEditor review={r} onDone={loadAll} />
              </div>
              <div className="flex gap-2">
                {r.approved ? (
                  <GhostButton data-testid={`unapprove-review-${r.id}`} onClick={() => setReviewApproval(r.id, false)} className="!px-4 !py-2 text-xs">
                    <X size={13} /> Unpublish
                  </GhostButton>
                ) : (
                  <PrimaryButton data-testid={`approve-review-${r.id}`} onClick={() => setReviewApproval(r.id, true)} className="!px-4 !py-2 text-xs">
                    <Check size={13} /> Publish
                  </PrimaryButton>
                )}
                <button data-testid={`delete-review-${r.id}`} onClick={() => remove("reviews", r.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Delete review">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "villa" && <VillaAdminPanel />}
      {tab === "food" && <FoodAdminPanel />}
      {tab === "security" && <SecurityPanel />}

      {tab === "excursions" && (
        <div className="space-y-4" data-testid="admin-excursions">
          <PrimaryButton data-testid="add-excursion-btn" onClick={() => openEditor("excursions", null)}>
            <Plus size={16} /> Add excursion
          </PrimaryButton>
          <div className="bg-white rounded-3xl border border-terracotta/10 p-6 overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className={th}>Excursion</th><th className={th}>Category</th><th className={th}>Price</th>
                  <th className={th}>Duration</th><th className={th}>Status</th><th className={th}></th>
                </tr>
              </thead>
              <tbody>
                {excursions.map((e) => (
                  <tr key={e.id} className="border-b border-ink/5" data-testid={`admin-excursion-row-${e.id}`}>
                    <td className={td}>
                      <div className="flex items-center gap-3">
                        <img src={e.image} alt="" className="w-12 h-12 rounded-xl object-cover" />
                        <div>
                          <p className="font-medium">{e.title}</p>
                          {e.title_es && <p className="text-xs text-teal italic">{e.title_es}</p>}
                        </div>
                      </div>
                    </td>
                    <td className={td}>{e.category}</td>
                    <td className={td}>${e.price}</td>
                    <td className={td}>{e.duration}</td>
                    <td className={td}><StatusBadge status={e.active ? "confirmed" : "cancelled"} /></td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      <button data-testid={`edit-excursion-${e.id}`} onClick={() => openEditor("excursions", e)} className="p-2 rounded-full hover:bg-sand transition-colors" aria-label="Edit"><Pencil size={15} /></button>
                      <button data-testid={`delete-excursion-${e.id}`} onClick={() => remove("excursions", e.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "restaurants" && (
        <div className="space-y-4" data-testid="admin-restaurants">
          <PrimaryButton data-testid="add-restaurant-btn" onClick={() => openEditor("restaurants", null)}>
            <Plus size={16} /> Add restaurant
          </PrimaryButton>
          <div className="bg-white rounded-3xl border border-terracotta/10 p-6 overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className={th}>Restaurant</th><th className={th}>Cuisine</th><th className={th}>Price</th>
                  <th className={th}>Area</th><th className={th}></th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((r) => (
                  <tr key={r.id} className="border-b border-ink/5" data-testid={`admin-restaurant-row-${r.id}`}>
                    <td className={td}><div className="flex items-center gap-3"><img src={r.image} alt="" className="w-12 h-12 rounded-xl object-cover" /><span className="font-medium">{r.name}</span></div></td>
                    <td className={td}>{r.cuisine}</td>
                    <td className={td}>{r.price_level}</td>
                    <td className={td}>{r.area}</td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      <button data-testid={`edit-restaurant-${r.id}`} onClick={() => openEditor("restaurants", r)} className="p-2 rounded-full hover:bg-sand transition-colors" aria-label="Edit"><Pencil size={15} /></button>
                      <button data-testid={`delete-restaurant-${r.id}`} onClick={() => remove("restaurants", r.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "blog" && (
        <div className="space-y-4" data-testid="admin-blog">
          <PrimaryButton data-testid="add-post-btn" onClick={() => openEditor("posts", null)}>
            <Plus size={16} /> New post
          </PrimaryButton>
          <div className="bg-white rounded-3xl border border-terracotta/10 p-6 overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className={th}>Post</th><th className={th}>Category</th><th className={th}>Published</th><th className={th}></th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.id} className="border-b border-ink/5" data-testid={`admin-post-row-${p.id}`}>
                    <td className={td}>
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt="" className="w-12 h-12 rounded-xl object-cover" />
                        <div>
                          <p className="font-medium">{p.title}</p>
                          {p.title_es && <p className="text-xs text-teal italic">{p.title_es}</p>}
                        </div>
                      </div>
                    </td>
                    <td className={td}>{p.category}</td>
                    <td className={td}>{fmtDate(p.created_at)}</td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      <button data-testid={`edit-post-${p.id}`} onClick={() => openEditor("posts", p)} className="p-2 rounded-full hover:bg-sand transition-colors" aria-label="Edit"><Pencil size={15} /></button>
                      <button data-testid={`delete-post-${p.id}`} onClick={() => remove("posts", p.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `${editing.item ? "Edit" : "New"} ${editing.type.slice(0, -1)}` : ""}
        testid="admin-editor-modal"
        wide
      >
        {editing && form && (
          <div className="space-y-4" data-testid="admin-editor-form">
            {editing.type === "excursions" && (
              <>
                {field("Title", "title")}
                {field("Título (Español)", "title_es", { placeholder: "Optional — shown to ES visitors" })}
                <div className="grid grid-cols-2 gap-4">
                  {field("Category", "category")}
                  {field("Price per person (USD)", "price", { type: "number", min: 0 })}
                </div>
                <div className="rounded-2xl border border-gold/30 bg-gold/5 p-4 space-y-3" data-testid="form-seasonal-uplifts">
                  <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45">Seasonal pricing (optional)</p>
                  <p className="text-xs text-ink/50 leading-relaxed">
                    Charge a percentage uplift when the excursion date falls inside a season (seasons are managed under Villa → Seasonal rates).
                  </p>
                  {(form.seasonal_uplifts || []).map((u, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`uplift-row-${i}`}>
                      <Select
                        value={u.period_id}
                        onChange={(e) => setForm({ ...form, seasonal_uplifts: form.seasonal_uplifts.map((x, j) => (j === i ? { ...x, period_id: e.target.value } : x)) })}
                        data-testid={`uplift-period-${i}`}
                      >
                        <option value="">Pick a season…</option>
                        {periods.map((p) => (
                          <option key={p.id} value={p.id}>{p.name} ({p.start} → {p.end})</option>
                        ))}
                      </Select>
                      <Input
                        type="number" min="1" max="200" placeholder="+ %" value={u.percent}
                        onChange={(e) => setForm({ ...form, seasonal_uplifts: form.seasonal_uplifts.map((x, j) => (j === i ? { ...x, percent: e.target.value } : x)) })}
                        data-testid={`uplift-percent-${i}`} className="!w-24 shrink-0"
                      />
                      <button
                        type="button" data-testid={`uplift-remove-${i}`} aria-label="Remove uplift"
                        onClick={() => setForm({ ...form, seasonal_uplifts: form.seasonal_uplifts.filter((_, j) => j !== i) })}
                        className="p-1.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {periods.length === 0 ? (
                    <p className="text-xs text-ink/40">No seasons yet — add them under Villa → Seasonal rates first.</p>
                  ) : (
                    <GhostButton
                      data-testid="uplift-add-btn" className="!px-4 !py-2 text-xs"
                      onClick={() => setForm({ ...form, seasonal_uplifts: [...(form.seasonal_uplifts || []), { period_id: "", percent: "" }] })}
                    >
                      <Plus size={13} /> Add seasonal uplift
                    </GhostButton>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {field("Duration", "duration", { placeholder: "3 hours" })}
                  <div>
                    <Label>Difficulty</Label>
                    <Select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} data-testid="form-difficulty-select">
                      {["Easy", "Beginner", "Moderate", "Advanced"].map((d) => <option key={d}>{d}</option>)}
                    </Select>
                  </div>
                </div>
                {field("Image URL", "image")}
                {field("What's included (comma separated)", "includes")}
                {field("Qué incluye (ES, separado por comas)", "includes_es")}
                {area("Description", "description", false, "description")}
                {area("Descripción (Español)", "description_es", false, "description")}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="form-active-checkbox" className="accent-terracotta w-4 h-4" />
                  Visible on the site
                </label>
              </>
            )}
            {editing.type === "restaurants" && (
              <>
                {field("Name", "name")}
                <div className="grid grid-cols-2 gap-4">
                  {field("Cuisine", "cuisine")}
                  <div>
                    <Label>Price level</Label>
                    <Select value={form.price_level} onChange={(e) => setForm({ ...form, price_level: e.target.value })} data-testid="form-price-level-select">
                      {["$", "$$", "$$$", "$$$$"].map((p) => <option key={p}>{p}</option>)}
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {field("Area", "area")}
                  {field("Recommended dish", "recommended_dish")}
                </div>
                {field("Plato recomendado (Español)", "recommended_dish_es")}
                {field("Image URL", "image")}
                {field("Google Maps link", "maps_url")}
                {area("Description", "description", false, "description")}
                {area("Descripción (Español)", "description_es", false, "description")}
              </>
            )}
            {editing.type === "posts" && (
              <>
                {field("Title", "title")}
                {field("Título (Español)", "title_es", { placeholder: "Optional — shown to ES visitors" })}
                <div className="grid grid-cols-2 gap-4">
                  {field("Category", "category")}
                  {field("Tags (comma separated)", "tags")}
                </div>
                {field("Image URL", "image")}
                {field("Excerpt", "excerpt")}
                {field("Extracto (Español)", "excerpt_es")}
                {area("Story (blank line between paragraphs)", "content", true, "blog")}
                {area("Historia (Español)", "content_es", true, "blog")}
              </>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <GhostButton data-testid="editor-cancel-btn" onClick={() => setEditing(null)}>Cancel</GhostButton>
              <PrimaryButton data-testid="editor-save-btn" onClick={saveEditor} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
